import { get, post } from './client'
import type { FifoExecutionStrategy } from './settings.api'

export type ModelItem   = { color: string; size: string; qty: number }
export type ModelGroup  = { model: string; totalQty: number; items: ModelItem[] }
export type VendorGroup = { vendor: string; totalQty: number; models: ModelGroup[] }
export type ModelGridResponse = { vendors: VendorGroup[]; totalSkus: number; totalItems: number }

export type PendingStockItem = {
  orderName:     string
  customerName:  string
  sku:           string
  shortageQty:   number
  shortageStart: string
}

export type OrderReservedItem = {
  sku:         string
  requiredQty: number
  reservedQty: number
  status:      'full' | 'partial' | 'none'
}

export type OrderReservedEntry = {
  orderName: string
  items:     OrderReservedItem[]
}

export type WarehouseEntry = {
  sku: string
  qty: number
}

export type FifoRunResult = {
  runId:                   string
  fullyReserved:           number
  fullyReservedItems:      number
  partiallyReserved:       number
  partiallyReservedItems:  number
  fullyShort:              number
  fullyShortItems:         number
  skipped:                 number
}

export type FifoStatusRecord = {
  lastRunAt:         string | null
  lastRunDurationMs: number | null
  isRunning:         boolean
  lastRunResult:     FifoRunResult | null
}

export type FifoStatusResponse = {
  config: {
    enabled:            boolean
    executionStrategy:  FifoExecutionStrategy
    scheduledTimes?:    string[]
    intervalHours?:     number
    staleReservedDays?: number
  }
  status:       FifoStatusRecord
  pendingCount: number
}

function qs(shop: string) {
  return `?shop=${encodeURIComponent(shop)}`
}

export const inventoryApi = {
  runFifo: (shop: string): Promise<FifoRunResult> =>
    post(`/inventory/fifo${qs(shop)}`, {}),

  getFifoStatus: (shop: string): Promise<FifoStatusResponse> =>
    get(`/inventory/fifo/status${qs(shop)}`),

  shortage: (shop: string): Promise<ModelGridResponse> =>
    get(`/inventory/shortage${qs(shop)}`),

  pendingStock: (shop: string): Promise<{ items: PendingStockItem[] }> =>
    get(`/inventory/pending-stock${qs(shop)}`),

  pickingList: (shop: string): Promise<ModelGridResponse> =>
    get(`/inventory/picking-list${qs(shop)}`),

  updateWarehouse: (shop: string, entries: WarehouseEntry[]): Promise<{ updated: number }> =>
    post(`/inventory/warehouse${qs(shop)}`, { entries }),

  orderReserved: (shop: string): Promise<{ orders: OrderReservedEntry[]; count: number }> =>
    get(`/inventory/order-reserved${qs(shop)}`),
}
